/**
 * LiveInvoicePreview — miniature mock invoice that updates in real time
 * as the user edits logo / brand color / company info on the
 * Invoices & Branding tab.
 *
 * Designed to live in a sticky right-rail on lg+ screens, and stack
 * below the form on mobile. Static fake line items + totals — only
 * the branded-looking bits (header band, accent color on totals, logo)
 * react to props.
 *
 * Keeps the user from having to "save settings → go create a test
 * invoice → see what it looks like" — a known friction point for
 * non-technical users setting up a new workspace.
 */
import { Eye } from 'lucide-react';

// Resolve the effective accent color from either a custom hex or
// the named-theme picker. Falls back to a neutral blue if nothing set.
function resolveAccent(customColor, themeId, themes) {
  if (customColor && /^#[0-9a-f]{3,8}$/i.test(customColor)) return customColor;
  const t = (themes || []).find((x) => x.id === themeId);
  return t?.primary || '#2563eb';
}

const SAMPLE_LINES = [
  { qty: 8,  label: 'Monthly bookkeeping',   rate: 175, amount: 1400 },
  { qty: 2,  label: 'Year-end tax prep',     rate: 350, amount: 700 },
  { qty: 1,  label: 'Quarterly review call', rate: 250, amount: 250 },
];

export default function LiveInvoicePreview({
  logo,
  customColor,
  themeId,
  themes,
  company,
}) {
  const accent = resolveAccent(customColor, themeId, themes);
  const companyName  = company?.name    || 'Your Company';
  const tagline      = company?.tagline || '';
  const email        = company?.email   || 'billing@yourcompany.com';
  const address      = company?.address || '';
  const phone        = company?.phone   || '';

  const subtotal = SAMPLE_LINES.reduce((s, l) => s + l.amount, 0);
  const tax      = Math.round(subtotal * 0.085);
  const total    = subtotal + tax;
  const fmt = (n) => `$${n.toLocaleString('en-US')}`;

  return (
    <div className="rounded-2xl border bg-card overflow-hidden shadow-sm" data-testid="invoice-live-preview">
      {/* Eyebrow */}
      <div className="px-4 py-2.5 bg-muted/40 border-b flex items-center gap-2 text-xs text-muted-foreground">
        <Eye className="w-3.5 h-3.5" />
        <span className="font-medium">Live preview</span>
        <span className="text-muted-foreground/70">— updates as you type</span>
      </div>

      {/* Invoice mock — scaled down */}
      <div className="bg-white text-zinc-900 p-5 text-[11px]" style={{ minHeight: 480 }}>
        {/* Branded header band */}
        <div className="h-1.5 rounded-full mb-4" style={{ background: accent }} />

        {/* Top row — logo + INVOICE label */}
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="min-w-0">
            {logo ? (
              <img
                src={logo}
                alt={`${companyName} logo`}
                className="max-h-12 max-w-[140px] object-contain mb-1"
                data-testid="preview-logo"
              />
            ) : (
              <div
                className="text-base font-bold leading-tight"
                style={{ color: accent }}
                data-testid="preview-company-name"
              >
                {companyName}
              </div>
            )}
            {tagline && (
              <p className="text-[10px] text-zinc-500 mt-0.5 italic line-clamp-1">{tagline}</p>
            )}
            <div className="text-[9px] text-zinc-500 mt-1.5 leading-tight">
              {address && <div>{address}</div>}
              <div>{email}</div>
              {phone && <div>{phone}</div>}
            </div>
          </div>
          <div className="text-right">
            <div
              className="text-lg font-bold tracking-wider"
              style={{ color: accent }}
            >
              INVOICE
            </div>
            <div className="text-[9px] text-zinc-500 mt-1 font-mono">#INV-2026-0042</div>
            <div className="text-[9px] text-zinc-500 mt-0.5">Due Apr 15, 2026</div>
          </div>
        </div>

        {/* Bill to */}
        <div className="mb-4 pb-3 border-b border-zinc-100">
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">Bill to</p>
          <p className="font-medium text-zinc-800">Acme Corporation</p>
          <p className="text-[10px] text-zinc-500">attn: Pat Williams · ap@acme.com</p>
        </div>

        {/* Line items table */}
        <table className="w-full text-[10px]">
          <thead>
            <tr style={{ borderBottom: `1.5px solid ${accent}` }}>
              <th className="text-left pb-1.5 font-semibold text-zinc-700">DESCRIPTION</th>
              <th className="text-right pb-1.5 font-semibold text-zinc-700 w-8">QTY</th>
              <th className="text-right pb-1.5 font-semibold text-zinc-700 w-14">RATE</th>
              <th className="text-right pb-1.5 font-semibold text-zinc-700 w-16">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {SAMPLE_LINES.map((line, i) => (
              <tr key={i} className="border-b border-zinc-100">
                <td className="py-1.5 text-zinc-700">{line.label}</td>
                <td className="py-1.5 text-right text-zinc-600 tabular-nums">{line.qty}</td>
                <td className="py-1.5 text-right text-zinc-600 tabular-nums">${line.rate}</td>
                <td className="py-1.5 text-right font-medium tabular-nums">{fmt(line.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mt-3">
          <div className="w-44 space-y-1 text-[10px]">
            <div className="flex justify-between text-zinc-600">
              <span>Subtotal</span>
              <span className="tabular-nums">{fmt(subtotal)}</span>
            </div>
            <div className="flex justify-between text-zinc-600">
              <span>Tax (8.5%)</span>
              <span className="tabular-nums">{fmt(tax)}</span>
            </div>
            <div
              className="flex justify-between text-white px-2 py-1.5 rounded-md mt-1 font-bold text-[11px]"
              style={{ background: accent }}
              data-testid="preview-total-band"
            >
              <span>Total Due</span>
              <span className="tabular-nums">{fmt(total)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-5 pt-3 border-t border-zinc-100 text-[9px] text-zinc-500 leading-relaxed">
          <strong className="text-zinc-700">Thank you for your business.</strong> Payment due within 30 days.
          Make checks payable to <strong>{companyName}</strong>, or pay online via the link in your email.
        </div>
      </div>
    </div>
  );
}
